import AddIcon from '@mui/icons-material/Add';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { Alert, Button, Grid2, Paper, Stack, Typography } from '@mui/material';
import { FormField, FormFieldType } from '@enroller/admissions-components';
import { useHeaderContext, ValidationType } from '../../../contexts/HeaderContext.tsx';
import { usePageTitle } from '@enroller/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { additionalPhotoDefaultValues, defaultValues, FamilyPhotosFormValues, familyPhotosSchema } from './FamilyPhotosFormValues.ts';

const MAX_PHOTO = 10;
const MAX_CHARACTERS_DESCRIPTION = 1000;

export const FamilyPhotosForm = () => {
  const { setSubmit, setHeaderTitle } = useHeaderContext();
  const { setPageTitle } = usePageTitle();
  const [validationMode, setValidationMode] = useState<ValidationType | ''>('');

  useEffect(() => {
    setHeaderTitle({ title: 'Photos', subtitle: 'Your Family photos' });
    setPageTitle('Photos');
  }, [setHeaderTitle, setPageTitle]);

  // Define resolver based on validation mode needed
  const resolver = useMemo(
    () => zodResolver(validationMode === ValidationType.Full ? familyPhotosSchema.required : familyPhotosSchema.optional),
    [validationMode],
  );

  const { control, handleSubmit } = useForm<FamilyPhotosFormValues>({
    defaultValues,
    resolver,
  });

  const {
    fields: additionalPhotos,
    append: addPhoto,
    remove: deletePhoto,
  } = useFieldArray({
    control,
    name: 'additionalPhotos',
  });

  const onSubmit = useCallback(
    () =>
      handleSubmit((data) => {
        // Format data
        console.log('Form submitted: ', data);
      })(),
    [handleSubmit],
  );

  // Submit form when resolver is changed.
  useEffect(() => {
    if (validationMode) {
      onSubmit();
      setValidationMode('');
    }
  }, [resolver, validationMode, onSubmit]);

  useEffect(() => {
    setSubmit(setValidationMode);
  }, [setSubmit, setValidationMode]);

  const isMaxPhotoLimitReached = additionalPhotos.length >= MAX_PHOTO;

  const handleAddPhoto = useCallback(() => {
    addPhoto(additionalPhotoDefaultValues);
  }, [addPhoto]);

  return (
    <Stack gap={2} component="form" onSubmit={onSubmit}>
      <Paper sx={{ p: 2 }}>
        <Grid2 container spacing={3}>
          <Grid2 size={12}>
            <Typography variant="subtitle1">Photo Album</Typography>
          </Grid2>
          <Grid2 size={12}>
            <AlbumInitialMessage />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              id="homeExteriorId"
              label="Your Home's Exterior and Grounds"
              control={control}
              type={FormFieldType.PictureUploader}
              name="homeExterior.photo"
            />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              rows={6}
              multiline
              control={control}
              label="Description"
              type={FormFieldType.Text}
              name={`homeExterior.description`}
              maxLength={MAX_CHARACTERS_DESCRIPTION}
            />
          </Grid2>
        </Grid2>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Grid2 container spacing={3}>
          <Grid2 size={12}>
            <FormField id="livingRoomId" label="Your Family or Living Room" control={control} type={FormFieldType.PictureUploader} name="livingRoom.photo" />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              rows={6}
              multiline
              control={control}
              label="Description"
              type={FormFieldType.Text}
              name={`livingRoom.description`}
              maxLength={MAX_CHARACTERS_DESCRIPTION}
            />
          </Grid2>
        </Grid2>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Grid2 container spacing={3}>
          <Grid2 size={12}>
            <FormField id="kitchenId" label="Kitchen" control={control} type={FormFieldType.PictureUploader} name="kitchen.photo" />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              rows={6}
              multiline
              control={control}
              label="Description"
              type={FormFieldType.Text}
              name={`kitchen.description`}
              maxLength={MAX_CHARACTERS_DESCRIPTION}
            />
          </Grid2>
        </Grid2>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Grid2 container spacing={3}>
          <Grid2 size={12}>
            <FormField id="studentBedroomId" label="Student's Bedroom" control={control} type={FormFieldType.PictureUploader} name="studentBedroom.photo" />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              rows={6}
              multiline
              control={control}
              label="Description"
              type={FormFieldType.Text}
              name={`studentBedroom.description`}
              maxLength={MAX_CHARACTERS_DESCRIPTION}
            />
          </Grid2>
        </Grid2>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Grid2 container spacing={3}>
          <Grid2 size={12}>
            <FormField id="studentBathroom" label="Student's Bathroom" control={control} type={FormFieldType.PictureUploader} name="studentBathroom.photo" />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              rows={6}
              multiline
              control={control}
              label="Description"
              type={FormFieldType.Text}
              name={`studentBathroom.description`}
              maxLength={MAX_CHARACTERS_DESCRIPTION}
            />
          </Grid2>
        </Grid2>
      </Paper>

      {additionalPhotos.map((item, index) => (
        <Paper key={item.id} sx={{ p: 2 }}>
          <Grid2 container spacing={3}>
            <Grid2 size={4}>
              <FormField control={control} label="What is this Photo of?" type={FormFieldType.Text} name={`additionalPhotos.${index}.title`} />
            </Grid2>
            <Grid2 size={12}>
              <FormField id={`photo-${index}-id`} control={control} type={FormFieldType.PictureUploader} name={`additionalPhotos.${index}.photo`} />
            </Grid2>
            <Grid2 size={12}>
              <FormField
                rows={6}
                multiline
                control={control}
                label="Description"
                type={FormFieldType.Text}
                name={`additionalPhotos.${index}.description`}
                maxLength={MAX_CHARACTERS_DESCRIPTION}
              />
            </Grid2>
            <Grid2 container>
              <Button variant="outlined" color="error" onClick={() => deletePhoto(index)} disabled={isMaxPhotoLimitReached}>
                Delete
              </Button>
            </Grid2>
          </Grid2>
        </Paper>
      ))}

      {isMaxPhotoLimitReached && (
        <Alert variant="outlined" severity="info">
          <Typography variant="caption" color="info">
            You have added the maximum number of {MAX_PHOTO} photos.
          </Typography>
        </Alert>
      )}

      <Grid2 container>
        <Button startIcon={<AddIcon />} variant="outlined" color="primary" onClick={handleAddPhoto} disabled={isMaxPhotoLimitReached}>
          Add Another Photo
        </Button>
      </Grid2>
    </Stack>
  );
};

const AlbumInitialMessage = () => (
  <Alert variant="outlined" severity="info">
    <Typography variant="body2">
      Please upload at least one photo for each category. You may upload up to 10 photos in total. Once uploaded, please describe the photo.
    </Typography>
  </Alert>
);
